import { useState, useEffect } from "react";
import { Box, Button, TextField, Tabs, Tab } from "@mui/material";
import {
  fetchProjects,
  fetchProjectCounts,
  createProject,
  updateProject,
  fetchProjectById,
  deleteProject,
  exportProjects,
} from "../../api/ProjectService";
import ZoneSelect from "../../components/common/ZoneSelect";
import CitySelect from "../../components/common/CitySelect";
import TableComponent from "../../components/Table";
import { toast } from "react-toastify";
import {
  Visibility,
  Add,
  Edit,
  Delete,
  Download as DownloadIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import theme from "../../theme/theme";
import { CreateProjectDialog } from "../../components/project/CreateProjectDialog";
import { EditProjectDialog } from "../../components/project/EditProjectDialog";
import ConfirmDialog from "../../components/ConfirmDialog";

const ProjectsManagementPage = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [links, setLinks] = useState([]);
  const [meta, setMeta] = useState({ from: 0, to: 0, total: 0 });
  const navigate = useNavigate();

  const [openCreate, setOpenCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [errors, setErrors] = useState({});

  // Edit state
  const [openEdit, setOpenEdit] = useState(false);
  const [editProject, setEditProject] = useState({});
  const [fetchingProject, setFetchingProject] = useState(false);
  const [updatingProject, setUpdatingProject] = useState(false);
  const [editErrors, setEditErrors] = useState({});

  const [filters, setFilters] = useState({ zone_id: "", city_id: "" });
  const [tabValue, setTabValue] = useState(0);
  const [counts, setCounts] = useState({ all: 0, no_active_service: 0 });

  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [exporting, setExporting] = useState(false);

  // Delete confirm state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadProjects = async () => {
    setLoading(true);
    try {
      const currentFilters = { ...filters, per_page: perPage };
      if (tabValue === 1) {
        currentFilters.service_status = "inactive";
      } else {
        delete currentFilters.service_status;
      }

      const response = await fetchProjects(
        currentPage,
        currentFilters,
        searchQuery || "",
      );
      // ProjectController returns the paginated object directly (not wrapped in {status, data})
      const payload = response.data ? response : { data: [], links: [], from: 0, to: 0, total: 0 };
      setProjects(Array.isArray(payload.data) ? payload.data : []);
      setLinks(payload.links || []);
      setMeta({ from: payload.from || 0, to: payload.to || 0, total: payload.total || 0 });
    } catch (error) {
      toast.error(error.message || "Failed to fetch projects");
    } finally {
      setLoading(false);
    }
  };

  const loadCounts = async () => {
    try {
      const response = await fetchProjectCounts(filters, searchQuery || "");
      setCounts({
        all: response.all ?? 0,
        no_active_service: response.no_active_service ?? 0,
      });
    } catch (error) {
      // Counts are non-critical — silently ignore
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const currentFilters = { ...filters };
      if (tabValue === 1) {
        currentFilters.service_status = "inactive";
      }
      const response = await exportProjects(currentFilters, searchQuery || "");
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "projects.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      toast.error("Failed to export projects");
    } finally {
      setExporting(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput);
  };

  const handleCreateProject = async (projectData) => {
    setCreating(true);
    setErrors({});
    try {
      await createProject(projectData);
      toast.success("Project created successfully");
      setOpenCreate(false);
      loadProjects();
      loadCounts();
    } catch (error) {
      if (error?.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        toast.error(error.message || "Failed to create project");
      }
    } finally {
      setCreating(false);
    }
  };

  const handleEditClick = async (row) => {
    setOpenEdit(true);
    setFetchingProject(true);
    setEditErrors({});
    try {
      const response = await fetchProjectById(row.id);
      setEditProject(response.data.data ? response.data.data : response.data);
    } catch (error) {
      toast.error("Failed to fetch project details");
    } finally {
      setFetchingProject(false);
    }
  };

  const handleUpdateProject = async (payload) => {
    setUpdatingProject(true);
    setEditErrors({});
    try {
      const data = payload || editProject;
      await updateProject(data.id, data);
      toast.success("Project updated successfully");
      setOpenEdit(false);
      loadProjects();
    } catch (error) {
      if (error?.response?.data?.errors) {
        setEditErrors(error.response.data.errors);
      } else {
        toast.error(error.message || "Failed to update project");
      }
    } finally {
      setUpdatingProject(false);
    }
  };

  const handleDeleteClick = (row) => {
    setDeleteTarget(row);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteProject(deleteTarget.id);
      toast.success("Project deleted successfully");
      setConfirmOpen(false);
      setDeleteTarget(null);
      loadProjects();
      loadCounts();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete project");
    } finally {
      setDeleting(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, tabValue, searchQuery, perPage]);

  useEffect(() => {
    loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, tabValue, searchQuery, currentPage, perPage]);

  useEffect(() => {
    loadCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, searchQuery]);

  const columns = [
    { field: "project_id", headerName: "Project ID" },
    { field: "project_name", headerName: "Project Name" },
    {
      field: "customer.name",
      headerName: "Customer Name",
    },
    {
      field: "zone.name",
      headerName: "Zone",
    },
    {
      field: "city.name",
      headerName: "City",
    },
    { field: "status", headerName: "Status" },
  ];

  const actions = [
    {
      icon: <Edit />,
      label: "Edit",
      onClick: (row) => handleEditClick(row),
      backgroundColor: theme.button.editBackgroundColor,
    },
    {
      icon: <Visibility />,
      label: "View",
      onClick: (row) => {
        navigate(`/projects/${row.id}`);
      },
      backgroundColor: theme.button.viewBackgroundColor,
    },
    {
      icon: <Delete />,
      label: "Delete",
      onClick: (row) => handleDeleteClick(row),
      backgroundColor: theme.button.deleteBackgroundColor,
    },
  ];

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box component="h1" sx={{ m: 0 }}>
          Projects Management
        </Box>
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
          <form
            onSubmit={handleSearchSubmit}
            style={{ display: "flex", gap: "8px" }}
          >
            <TextField
              size="small"
              placeholder="Search projects..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              sx={{ backgroundColor: "#fff", borderRadius: 1 }}
            />
            <Button
              type="submit"
              variant="contained"
              sx={{ minWidth: "40px", p: 1 }}
            >
              <SearchIcon />
            </Button>
          </form>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={exporting}
            sx={{ backgroundColor: "#fff" }}
          >
            {exporting ? "Exporting..." : "Export Excel"}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            sx={{
              borderRadius: theme.button.borderRadius,
              backgroundColor: theme.palette.primary.main,
            }}
            onClick={() => setOpenCreate(true)}
          >
            Create Project
          </Button>
        </Box>
      </Box>

      {/* Tabs for toggling project types */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="project filter tabs"
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab label={`All Projects (${counts.all})`} />
          <Tab label={`No Active Service (${counts.no_active_service})`} />
        </Tabs>
      </Box>

      {/* Real-time Data Filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <ZoneSelect
          label="Filter by Zone"
          value={filters.zone_id}
          onChange={(id) => setFilters({ zone_id: id, city_id: "" })}
          sx={{ minWidth: 200 }}
        />

        <CitySelect
          label="Filter by City"
          zoneId={filters.zone_id}
          value={filters.city_id}
          onChange={(id) => setFilters({ ...filters, city_id: id })}
          sx={{ minWidth: 200 }}
        />
      </Box>

      <Box>
        <TableComponent
          data={projects || []}
          columns={columns}
          actions={actions}
          loading={loading}
          links={links}
          setCurrentPage={setCurrentPage}
          meta={meta}
          perPage={perPage}
          setPerPage={setPerPage}
        />
      </Box>

      <CreateProjectDialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        saving={creating}
        onSave={handleCreateProject}
        errors={errors}
      />

      <EditProjectDialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        loading={fetchingProject}
        saving={updatingProject}
        project={editProject}
        setProject={setEditProject}
        onSave={handleUpdateProject}
        errors={editErrors}
      />

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setDeleteTarget(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Project"
        message={`Are you sure you want to delete project #${deleteTarget?.project_id}? This action cannot be undone.`}
        loading={deleting}
      />
    </Box>
  );
};

export default ProjectsManagementPage;
